import React, { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from 'app/components/ui/dialog';
import { Button } from 'app/components/ui/button';
import { useProductsSlice } from '../../slice';
import { CategoriesForm } from '../form';
import { useToast } from 'app/components/ui/use-toast';
import { Form } from 'app/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

// Validation schema
const categorySchema = z.object({
  category_name: z
    .string()
    .min(2, 'Category name must be at least 2 characters')
    .max(100, 'Category name must be less than 100 characters'),
  slug: z
    .string()
    .min(2, 'Slug must be at least 2 characters')
    .regex(
      /^[a-z0-9-]+$/,
      'Slug can only contain lowercase letters, numbers, and hyphens',
    ),
  parent_category_id: z.string().optional(),
  image: z.string().optional(),
});

type CategoryFormData = z.infer<typeof categorySchema>;

interface DialogAddCategoryProps {
  isOpen: boolean;
  onClose: (isOpen: boolean) => void;
}

const DialogAddCategory: React.FC<DialogAddCategoryProps> = ({
  isOpen,
  onClose,
}) => {
  const { useAddProductsCategoriesMutation } = useProductsSlice();
  const [addCategory, { isLoading, isError, error, data }] =
    useAddProductsCategoriesMutation();
  const { toast } = useToast();

  const form = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      category_name: '',
      slug: '',
      parent_category_id: '0',
      image: '',
    },
  });

  // Handle successful submission
  useEffect(() => {
    if (data && !isError) {
      toast({
        title: 'Success',
        description: data?.message || 'Category created successfully',
        variant: 'default',
      });
      form.reset();
      onClose(false);
    }
  }, [data, isError, form, onClose, toast]);

  // Handle errors
  useEffect(() => {
    if (isError && error) {
      const errorMessage =
        'data' in error && error.data?.message
          ? error.data.message
          : 'Failed to create category. Please try again.';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  }, [isError, error, toast]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      form.reset();
    }
  }, [isOpen, form]);

  const onSubmit = async (formData: CategoryFormData) => {
    try {
      const payload = {
        category_name: formData.category_name.trim(),
        slug: formData.slug.trim(),
        parent_category_id:
          formData.parent_category_id === '0'
            ? null
            : parseInt(formData.parent_category_id || '0'),
        image: formData.image || null,
      };

      await addCategory(payload).unwrap();
    } catch (error) {
      // Error handling is done in useEffect
      console.error('Category creation error:', error);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            Add New Category
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <CategoriesForm />

            <DialogFooter className="gap-2 px-5 pb-5">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="min-w-[100px]"
              >
                {isLoading ? 'Creating...' : 'Create Category'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default DialogAddCategory;
